import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Home,
  TrendingUp,
  Library,
  Upload,
  LogOut,
  Shield,
  ShieldCheck,
  PenTool,
  PenLine,
  FileText,
  Headphones,
  Menu,
  X,
  User as UserIcon,
  LayoutDashboard,
  Crown,
  Lock,
  Sparkles,
  Gift,
  BarChart3,
  Users as UsersIcon,
} from 'lucide-react';
import { Button } from './ui/button';
import HeaderCoinBadge from './HeaderCoinBadge';
import NotificationsBell from './NotificationsBell';
import { checkAuth, logout as authLogout } from '../utils/authUtils';
import WhatsNewModal from './WhatsNewModal';
import WriterContractPopup from './WriterContractPopup';
import WriterContractSignModal from './WriterContractSignModal';
import AdminVaultNotifier from './AdminVaultNotifier';
import ForceRefreshButton from './ForceRefreshButton';
import VoiceNavButton from './VoiceNavButton';
import AnnouncementBanner from './AnnouncementBanner';
import InstallAppButton from './InstallAppButton';
import InstallPromptBanner from './InstallPromptBanner';  // eslint-disable-line no-unused-vars -- kept for backwards import-site compatibility; superseded by <InstallPWA /> in App.js
import SpaceToggle from './SpaceToggle';
import AddPasskeyPill from './AddPasskeyPill';
import AdminRoleToggle from './AdminRoleToggle';
import GrantCoinsDialog from './GrantCoinsDialog';
import { effectiveRoleFor, onSpaceChange, getActiveSpace, getAdminViewingAs, setAdminViewingAs, setActiveSpace } from '../utils/spaceContext';
import { PAYMENTS_DISABLED } from '../utils/paymentAvailability';

import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import useStreakHeartbeat from '../hooks/useStreakHeartbeat';
import OnboardingTour from './OnboardingTour';

/**
 * Role-aware bottom tab configuration.
 * All roles now share the same 4-tab layout — Profile · Discover · Progress · Library —
 * with the Profile icon swapping to reflect the current effective role.
 */
const PROFILE_ICON_BY_ROLE = {
  user: BookOpen,
  writer: PenLine,
  admin: ShieldCheck,
};

const buildTabsFor = (role) => [
  { key: 'profile', path: '/profile', label: 'Profile', Icon: PROFILE_ICON_BY_ROLE[role] || BookOpen },
  { key: 'discover', path: '/discover', label: 'Discover', Icon: Sparkles },
  { key: 'progress', path: '/progress', label: 'Progress', Icon: TrendingUp },
  { key: 'library', path: '/library', label: 'Library', Icon: Library },
];

/**
 * Admin-only bottom tabs — reshaped Feb 28 2026 per user spec:
 *   "Admins Panel → Scribe Profile → Reader Profile → Library".
 *
 * Each Writer/Reader tab flips `adminViewingAs` so /profile renders
 * with that persona's stats panel + button list. Admins Panel resets
 * the view back to admin god-mode.
 */
const ADMIN_TABS = [
  { key: 'admin-panel',    path: '/admin-panel', label: 'Admins Panel',   Icon: ShieldCheck, viewAs: 'admin'  },
  { key: 'writer-profile', path: '/profile',     label: 'Scribe Profile', Icon: PenLine,     viewAs: 'writer' },
  { key: 'reader-profile', path: '/profile',     label: 'Reader Profile', Icon: BookOpen,    viewAs: 'reader' },
  { key: 'library',        path: '/library',     label: 'Library',        Icon: Library     },
];

/**
 * Co-admin bottom tabs — Jun 4 2026. Read-only mirror of ADMIN_TABS minus
 * Admins Panel (renamed to "Admin (read-only)") and Upload (co-admins can't
 * upload). They still get to flip into Writer/Reader spaces to QA, but the
 * default landing is their own admin-style profile (`viewAs: 'admin'`).
 */
const COADMIN_TABS = [
  { key: 'admin-panel',    path: '/admin',   label: 'Co-admin Panel', Icon: ShieldCheck, viewAs: 'admin'  },
  { key: 'writer-profile', path: '/profile', label: 'Scribe Profile', Icon: PenLine,     viewAs: 'writer' },
  { key: 'reader-profile', path: '/profile', label: 'Reader Profile', Icon: BookOpen,    viewAs: 'reader' },
  { key: 'library',        path: '/library', label: 'Library',        Icon: Library     },
];

/**
 * Scribe-only bottom tabs — Feb 28 2026 user spec:
 *   "Scribe profile, reader profile, upload and library … move
 *    everything accordingly like in admin's writer and reader."
 *
 * `writerSpace` flips `writerActiveSpace` so /profile (and any other
 * persona-aware page) renders the right hero. Mirrors the admin tabs
 * but uses the scribe-space toggle instead of adminViewingAs.
 */
const WRITER_TABS = [
  { key: 'writer-profile', path: '/profile', label: 'Scribe Profile', Icon: PenLine,  writerSpace: 'writer' },
  { key: 'reader-profile', path: '/profile', label: 'Reader Profile', Icon: BookOpen, writerSpace: 'reader' },
  { key: 'upload',         path: '/upload',  label: 'Upload',         Icon: Upload },
  { key: 'library',        path: '/library', label: 'Library',        Icon: Library },
];

/**
 * Context strip — the secondary row under the brand. Its contents change
 * based on which BottomBar tab the user is on, so the top is never crowded
 * with controls that don't apply to the current page.
 *
 *   /profile  → Admin · Reader · Writer (the role toggle, admin only)
 *   /discover → Onboarding · Placement Tests
 *   /progress → Core Skills · Coins · Achievements
 *   /library  → Books · Audios
 *
 * Each chip writes a `?ctx=…` (or `?type=…` for library) search param so
 * the underlying page can react. Pages that haven't wired a consumer yet
 * still benefit because the chip acts as a deep-link hint.
 */
const CTX_STRIP_BY_PATH = {
  '/profile': 'profile',
  '/discover': 'discover',
  '/progress': 'progress',
  // /library — REMOVED June 2 2026. The Library page now owns its own
  // 2-pill gate; AppShell no longer renders a redundant Books/Audios
  // sub-row on top of it.
};

const TopBarContextStrip = ({ realRole, onOpenGrantDialog }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const which = CTX_STRIP_BY_PATH[location.pathname];
  if (!which) return null;

  const params = new URLSearchParams(location.search);
  const setParam = (key, value) => {
    const next = new URLSearchParams(location.search);
    if (value == null) next.delete(key);
    else next.set(key, value);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: false });
  };
  const isActive = (key, value) => params.get(key) === value;

  // Profile → role toggle (admin only). Non-admin readers/scribes don't
  // need a sub-row here because the page itself already shows their role.
  if (which === 'profile') {
    if (realRole !== 'admin' && realRole !== 'coadmin' && realRole !== 'king') return null;
    const isCoadmin = realRole === 'coadmin' || realRole === 'king';
    return (
      <div
        className="border-t border-white/10 px-3 pb-1.5 pt-1.5 flex items-center justify-center gap-2"
        data-testid="topbar-context-profile"
      >
        <AdminRoleToggle noNavigate />
        {!isCoadmin && onOpenGrantDialog && (
          <button
            type="button"
            onClick={() => onOpenGrantDialog()}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-purple-600/15 border border-purple-500/40 text-purple-200 hover:bg-purple-600/30 hover:text-white text-xs font-bold transition-colors"
            data-testid="admin-gift-coins-trigger"
            title="Gift coins (admin)"
          >
            <Gift className="w-3.5 h-3.5" />
            Gift
          </button>
        )}
      </div>
    );
  }

  let chips = [];
  if (which === 'discover') {
    chips = [
      { key: 'onboarding',   label: 'Onboarding',      icon: Sparkles, accent: 'bg-indigo-600/90 ring-indigo-400/40' },
      { key: 'placement',    label: 'Placement Tests', icon: FileText, accent: 'bg-purple-600/90 ring-purple-400/40' },
    ];
  } else if (which === 'progress') {
    chips = [
      { key: 'skills',       label: 'Core Skills',     icon: TrendingUp, accent: 'bg-emerald-600/90 ring-emerald-400/40' },
      { key: 'coins',        label: 'Coins',           icon: Crown,      accent: 'bg-amber-600/90 ring-amber-400/40' },
      { key: 'achievements', label: 'Achievements',    icon: Sparkles,   accent: 'bg-pink-600/90 ring-pink-400/40' },
    ];
  } else if (which === 'library') {
    chips = [
      { key: 'ebook',        label: 'Books',           icon: BookOpen,    accent: 'bg-indigo-600/90 ring-indigo-400/40',  paramKey: 'type' },
      { key: 'audiobook',    label: 'Audios',          icon: Headphones,  accent: 'bg-fuchsia-600/90 ring-fuchsia-400/40', paramKey: 'type' },
    ];
  }

  return (
    <div
      className="border-t border-white/10 px-3 pb-1.5 pt-1.5 flex items-center gap-2 overflow-x-auto no-scrollbar"
      data-testid={`topbar-context-${which}`}
    >
      {chips.map(({ key, label, icon: Icon, accent, paramKey = 'section' }) => {
        const active = isActive(paramKey, key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => setParam(paramKey, active ? null : key)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ring-1 transition-all ${
              active ? `${accent} text-white shadow-md` : 'bg-white/5 text-slate-300 ring-white/10 hover:bg-white/10'
            }`}
            data-testid={`topbar-ctx-${which}-${key}`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
};

const BottomBar = ({ role, realRole, adminViewing }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Admins ALWAYS get the reshaped 4-tab admin nav (Admins Panel · Writer
  // Profile · Reader Profile · Library), even while peeking the app as a
  // reader/writer — so they can flip back to god-mode in one tap.
  // Writers (when in writer-space) get their own 4-tab nav per Feb 28
  // user spec — Scribe Profile · Reader Profile · Upload · Library.
  const isAdmin = realRole === 'admin';
  const isCoadmin = realRole === 'coadmin' || realRole === 'king';
  const isWriter = realRole === 'writer';
  const tabs = isAdmin
    ? ADMIN_TABS
    : isCoadmin
      ? COADMIN_TABS
      : isWriter
        ? WRITER_TABS
        : buildTabsFor(role);
  // Safelist friendly: we only ever render 3-4 tabs so this is static.
  const gridCls = tabs.length === 4 ? 'grid-cols-4' : tabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3';

  const isAdminTabActive = (tab) => {
    if (tab.key === 'admin-panel') {
      // Match BOTH /admin and /admin-panel so the same active rule works
      // for the admin's "Admins Panel" tab (→ /admin-panel) and the
      // co-admin's "Co-admin Panel" tab (→ /admin).
      return (location.pathname === tab.path || location.pathname === '/admin') && adminViewing === 'admin';
    }
    if (tab.key === 'writer-profile') {
      return location.pathname === '/profile' && adminViewing === 'writer';
    }
    if (tab.key === 'reader-profile') {
      return location.pathname === '/profile' && adminViewing === 'reader';
    }
    return location.pathname === tab.path;
  };

  // Writer-tab active state uses writerActiveSpace instead of
  // adminViewingAs. Same shape, different axis.
  const isWriterTabActive = (tab) => {
    const activeSpace = getActiveSpace();
    if (tab.key === 'writer-profile') {
      return location.pathname === '/profile' && activeSpace === 'writer';
    }
    if (tab.key === 'reader-profile') {
      return location.pathname === '/profile' && activeSpace === 'reader';
    }
    return location.pathname === tab.path;
  };

  const isActive = (path) =>
    location.pathname === path ||
    (path === '/dashboard' && location.pathname === '/');

  const handleAdminTabClick = (tab) => {
    // Persist viewing-as so AppShell re-renders banners + Profile.jsx picks
    // up the right persona before navigation lands.
    if (typeof tab.viewAs !== 'undefined') {
      setAdminViewingAs(tab.viewAs);
    }
    navigate(tab.path);
  };

  const handleWriterTabClick = (tab) => {
    // Flip writerActiveSpace BEFORE navigating so the destination renders
    // the right persona on first paint.
    if (typeof tab.writerSpace !== 'undefined') {
      setActiveSpace(tab.writerSpace);
    }
    navigate(tab.path);
  };

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-[#0a0b14]/95 backdrop-blur-xl border-t border-white/10"
      data-testid="app-shell-bottom-bar"
      data-role={role}
    >
      <div className={`grid ${gridCls} max-w-xl mx-auto`}>
        {tabs.map((tab) => {
          const { key, label, Icon } = tab;
          // Co-admin uses the same admin tab semantics (viewAs flips adminViewingAs).
          const useAdminSemantics = isAdmin || isCoadmin;
          const active = useAdminSemantics
            ? isAdminTabActive(tab)
            : isWriter
              ? isWriterTabActive(tab)
              : isActive(tab.path);
          const onTabClick = useAdminSemantics
            ? () => handleAdminTabClick(tab)
            : isWriter
              ? () => handleWriterTabClick(tab)
              : () => navigate(tab.path);
          return (
            <button
              key={key}
              onClick={onTabClick}
              data-testid={`bottom-tab-${key}`}
              className={`relative flex flex-col items-center gap-1 py-3 transition-colors ${
                active
                  ? 'text-indigo-400'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <div
                className={`transition-transform ${
                  active ? 'scale-110' : 'scale-100'
                }`}
              >
                <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
              </div>
              <span
                className={`text-[11px] font-semibold ${
                  active ? 'text-indigo-300' : ''
                }`}
              >
                {label}
              </span>
              {active && (
                <span className="absolute top-0 w-10 h-0.5 rounded-b-full bg-gradient-to-r from-indigo-400 to-purple-500" />
              )}
            </button>
          );
        })}
      </div>
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
};

/**
 * Per-role secondary actions (shown on desktop inline, on mobile in a drawer).
 */
const RoleActions = ({ role, user, onLogout, inline = false }) => {
  const navigate = useNavigate();
  const itemCls = inline
    ? 'text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-colors'
    : 'w-full text-sm px-4 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors';

  const actions = [];

  if (role === 'admin') {
    actions.push(
      <button
        key="admin-panel"
        onClick={() => navigate('/admin')}
        className={`${itemCls} bg-red-600 text-white hover:bg-red-700`}
        data-testid="header-action-admin-panel"
      >
        <Shield className="w-4 h-4" />
        Admin Panel
      </button>,
    );
    // Quick-jump shortcuts to the most-used admin tabs — added Feb 26 2026
    // per user request ("Put admin Analytics to show all analytics and
    // users on the menu at the top right"). Both jump straight into the
    // already-existing tabs inside /admin via location.state so we don't
    // duplicate any UI.
    actions.push(
      <button
        key="admin-analytics"
        onClick={() => navigate('/admin', { state: { tab: 'analytics' } })}
        className={`${itemCls} bg-cyan-600 text-white hover:bg-cyan-700`}
        data-testid="header-action-admin-analytics"
      >
        <BarChart3 className="w-4 h-4" />
        Analytics
      </button>,
    );
    actions.push(
      <button
        key="admin-users"
        onClick={() => navigate('/admin', { state: { tab: 'users' } })}
        className={`${itemCls} bg-emerald-600 text-white hover:bg-emerald-700`}
        data-testid="header-action-admin-users"
      >
        <UsersIcon className="w-4 h-4" />
        Users
      </button>,
    );
    actions.push(
      <span
        key="admin-badge"
        className={`${itemCls} bg-red-600/20 text-red-300 border border-red-600/50 cursor-default`}
      >
        <Crown className="w-3.5 h-3.5" />
        HIGH KING
      </span>,
    );
    actions.push(
      <button
        key="upload"
        onClick={() => navigate('/upload')}
        className={`${itemCls} bg-indigo-600 text-white hover:bg-indigo-700`}
        data-testid="header-action-upload"
      >
        <Upload className="w-4 h-4" />
        Upload
      </button>,
    );
    // Admin God Mode — admins always have full reader access to the Vault.
    actions.push(
      <button
        key="vault"
        onClick={() => navigate('/vault')}
        className={`${itemCls} bg-fuchsia-600/90 text-white hover:bg-fuchsia-700`}
        data-testid="header-action-vault"
      >
        <Lock className="w-4 h-4" />
        Vault
      </button>,
    );
  } else if (role === 'coadmin' || role === 'king') {
    // King (formerly Co-admin) gets a slim header: read-only admin
    // panel + analytics + users shortcuts + a Throne Room link to
    // appoint Ambassadors. NO Upload, NO Vault, NO Premium pill.
    actions.push(
      <button
        key="admin-panel"
        onClick={() => navigate('/admin')}
        className={`${itemCls} bg-amber-600 text-white hover:bg-amber-700`}
        data-testid="header-action-admin-panel"
      >
        <Shield className="w-4 h-4" />
        Admin Panel
      </button>,
    );
    actions.push(
      <button
        key="king-throne"
        onClick={() => navigate('/king')}
        className={`${itemCls} bg-amber-500/80 text-white hover:bg-amber-600`}
        data-testid="header-action-king-throne"
      >
        <Crown className="w-4 h-4" />
        Throne Room
      </button>,
    );
    actions.push(
      <button
        key="admin-analytics"
        onClick={() => navigate('/admin', { state: { tab: 'analytics' } })}
        className={`${itemCls} bg-cyan-500/80 text-white hover:bg-cyan-600`}
        data-testid="header-action-admin-analytics"
      >
        <BarChart3 className="w-4 h-4" />
        Analytics
      </button>,
    );
    actions.push(
      <button
        key="admin-users"
        onClick={() => navigate('/admin', { state: { tab: 'users' } })}
        className={`${itemCls} bg-emerald-600 text-white hover:bg-emerald-700`}
        data-testid="header-action-admin-users"
      >
        <UsersIcon className="w-4 h-4" />
        Users
      </button>,
    );
    actions.push(
      <span
        key="king-badge"
        className={`${itemCls} bg-amber-500/20 text-amber-300 border border-amber-500/50 cursor-default`}
      >
        <Crown className="w-3.5 h-3.5" />
        KING
      </span>,
    );
  } else if (role === 'writer') {
    actions.push(
      <span
        key="writer-badge"
        className={`${itemCls} bg-purple-600/20 text-purple-300 border border-purple-600/50 cursor-default`}
      >
        <PenTool className="w-3.5 h-3.5" />
        WRITER
      </span>,
    );
    actions.push(
      <button
        key="upload"
        onClick={() => navigate('/upload')}
        className={`${itemCls} bg-indigo-600 text-white hover:bg-indigo-700`}
        data-testid="header-action-upload"
      >
        <Upload className="w-4 h-4" />
        Upload
      </button>,
    );
  } else {
    // reader
    if (user?.vault_access_granted) {
      actions.push(
        <button
          key="vault"
          onClick={() => navigate('/vault')}
          className={`${itemCls} bg-fuchsia-600/90 text-white hover:bg-fuchsia-700`}
          data-testid="header-action-vault"
        >
          <Lock className="w-4 h-4" />
          Vault
        </button>,
      );
    }
    actions.push(
      <button
        key="premium"
        onClick={() => navigate('/premium')}
        className={`${itemCls} bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 hover:from-amber-600 hover:to-orange-600`}
        data-testid="header-action-premium"
      >
        <Crown className="w-4 h-4" />
        Premium
      </button>,
    );
  }

  // Profile icon/link for everyone
  actions.push(
    <button
      key="profile"
      onClick={() => navigate('/profile')}
      className={`${itemCls} bg-white/5 text-gray-200 hover:bg-white/10`}
      data-testid="header-action-profile"
    >
      {user?.picture ? (
        <img src={user.picture} alt={user.name} className="w-4 h-4 rounded-full" />
      ) : (
        <UserIcon className="w-4 h-4" />
      )}
      <span className={inline ? 'hidden lg:inline' : ''}>{user?.name || 'Profile'}</span>
    </button>,
  );

  actions.push(
    <button
      key="logout"
      onClick={onLogout}
      className={`${itemCls} border border-white/15 text-gray-200 hover:bg-white/10`}
      data-testid="header-action-logout"
    >
      <LogOut className="w-4 h-4" />
      Logout
    </button>,
  );

  return <>{actions}</>;
};

const TopBar = ({ role, realRole, user, onLogout, onOpenGrantDialog }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const drawerCloseRef = useRef(null);

  // The compact navigation is used below the `lg` breakpoint, so its dialog
  // must work equally well on phones and portrait tablets. Keep focus inside
  // while it is open, restore it to the trigger when it closes, and let
  // Escape dismiss it without changing any of the menu actions or routes.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const trigger = menuButtonRef.current;
    const getFocusable = () =>
      Array.from(
        drawerRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      ).filter((element) => element.getAttribute('aria-hidden') !== 'true');

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const focusFrame = window.requestAnimationFrame(() => drawerCloseRef.current?.focus());
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      trigger?.focus();
    };
  }, [menuOpen]);

  // A drawer opened in portrait can otherwise remain logically open after a
  // resize to the desktop layout, where desktop actions replace its trigger.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeAtDesktop = () => {
      if (desktop.matches) setMenuOpen(false);
    };
    closeAtDesktop();
    if (desktop.addEventListener) {
      desktop.addEventListener('change', closeAtDesktop);
      return () => desktop.removeEventListener('change', closeAtDesktop);
    }
    desktop.addListener(closeAtDesktop);
    return () => desktop.removeListener(closeAtDesktop);
  }, []);

  return (
    <>
      <header
        className="sticky top-0 z-50 bg-[#0a0b14]/90 backdrop-blur-xl border-b border-white/10"
        style={{
          // iOS standalone-PWA: lift the bar BELOW the Dynamic Island /
          // status bar. `max(...)` guarantees at least 16px of top padding
          // even when env() returns 0 (Android, desktop, Safari non-standalone
          // mode) — Feb 26 2026 bumped from 8→16 because the user reported
          // the brand still sitting flush against the notch on iOS Safari
          // (non-PWA) where env() collapses to 0 and 8px wasn't enough to
          // clear the URL-bar overlap during scroll.
          paddingTop: 'max(env(safe-area-inset-top, 0px), 16px)',
          paddingLeft: 'max(env(safe-area-inset-left, 0px), 4px)',
          paddingRight: 'max(env(safe-area-inset-right, 0px), 4px)',
        }}
        data-testid="app-shell-top-bar"
        data-role={role}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 min-h-[56px] flex items-center justify-between gap-3">
          {/* Brand — 44px min hit target. */}
          <button
            onClick={() => navigate(role === 'admin' ? '/admin-panel' : role === 'writer' ? '/writer-dashboard' : '/dashboard')}
            className="shrink-0 flex items-center gap-2 min-w-0 min-h-[44px] py-2 -my-1"
            data-testid="app-shell-brand"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-500 flex items-center justify-center shrink-0">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm sm:text-base font-bold text-white truncate hidden xs:inline">
              <span className="sm:hidden">Smarter Hub</span>
              <span className="hidden sm:inline">Smarter Learning Hub</span>
            </span>
          </button>

          {/* Right cluster — main row. Kept lean: brand/coin/bell/install/
              hamburger only. Role-specific tools (Admin role toggle, gift
              coins, etc.) live in the context strip below or in the mobile
              drawer so this row never crowds out the bell/hamburger on a
              430px viewport. */}
          <div className="flex items-center gap-2 flex-nowrap min-w-0">
            {/* Scribe-only: flip between Writer and Reader spaces. Always
                visible on every page so the identity swap is one tap away. */}
            {realRole === 'writer' && <SpaceToggle compact />}

            {/* Coin badge — only meaningful for readers (effective role). */}
            {role === 'user' && <HeaderCoinBadge />}

            {/* In-app notifications bell — visible to readers, scribes and admins. */}
            <NotificationsBell />

            {/* Nudge to enrol a passkey when none exist — hides once enrolled. */}
            <div className="hidden sm:block">
              <AddPasskeyPill inline />
            </div>

            {/* Install-app prompt — visible on every role when the browser
                says the PWA is installable (Android/desktop) or it's iOS Safari. */}
            <div className="hidden sm:block">
              <InstallAppButton variant="compact" />
            </div>

            {/* Desktop inline actions — only on lg+ (≥1024 px). Phones
                AND tablets in portrait fall back to the hamburger drawer
                so Vault/Premium/Profile/Logout don't crowd the row.
                Feb 22 2026 bumped from md → lg per user request. */}
            <div className="hidden lg:flex items-center gap-2">
              <RoleActions role={role} user={user} onLogout={onLogout} inline />
            </div>

            {/* Mobile/tablet hamburger — 44×44 hit target per iOS HIG so it's
                reliably tappable near the screen edge. */}
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden p-3 -mr-1 rounded-lg hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="app-shell-mobile-drawer"
              data-testid="app-shell-menu-btn"
            >
              <Menu className="w-5 h-5 text-gray-200" />
            </button>
          </div>
        </div>

        {/* Context-aware secondary row. Renders the controls relevant to
            the current BottomBar tab so the top bar is never crowded with
            options that don't apply: Profile→role toggle, Discover→
            onboarding/placement, Progress→skills/coins/achievements,
            Library→books/audios. See <TopBarContextStrip />. */}
        <TopBarContextStrip realRole={realRole} onOpenGrantDialog={onOpenGrantDialog} />
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-50 lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <aside
            ref={drawerRef}
            id="app-shell-mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="app-shell-mobile-drawer-title"
            className="fixed top-0 right-0 h-full w-72 bg-[#0a0b14] border-l border-white/10 z-50 lg:hidden flex flex-col"
            style={{
              // Same safe-area lift as the top bar so the drawer's close
              // button isn't trapped under the Dynamic Island.
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            data-testid="app-shell-mobile-drawer"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 id="app-shell-mobile-drawer-title" className="text-sm font-semibold text-white">
                {user?.name || 'Menu'}
              </h2>
              <button
                ref={drawerCloseRef}
                type="button"
                onClick={() => setMenuOpen(false)}
                className="p-1.5 rounded-md hover:bg-white/10"
                aria-label="Close menu"
              >
                <X className="w-4 h-4 text-gray-300" />
              </button>
            </div>
            <div
              className="p-3 space-y-2 overflow-y-auto"
              onClick={() => setMenuOpen(false)}
            >
              {/* The install-app button opens its own modal — stop the
                  drawer's auto-close so the modal isn't immediately
                  unmounted along with the button that opened it. */}
              <div
                className="sm:hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <InstallAppButton variant="full" />
              </div>
              {/* Force-update button — surfaced prominently here so users
                  stuck on a stale Cloudflare/SW cache can self-heal in one
                  tap without diving into Safari Settings. Stop propagation
                  so the drawer stays open while the SW unregister + cache
                  wipe runs. */}
              <div onClick={(e) => e.stopPropagation()}>
                <ForceRefreshButton variant="full" />
              </div>
              {/* Scribe-only: space switcher (full-width variant). */}
              {realRole === 'writer' && (
                <div onClick={(e) => e.stopPropagation()}>
                  <SpaceToggle />
                </div>
              )}
              {/* Add-passkey CTA for users who haven't enrolled yet. */}
              <div onClick={(e) => e.stopPropagation()}>
                <AddPasskeyPill />
              </div>
              <RoleActions role={role} user={user} onLogout={onLogout} />
            </div>
          </aside>
        </>
      )}
    </>
  );
};

/**
 * AppShell wraps every authenticated page with:
 *  - sticky top bar (brand + role-aware actions + logout)
 *  - bottom tab bar (role-aware: Home/Progress/Library for readers,
 *    Dashboard/Upload/Library for writers, Dashboard/Content/Library for admins)
 *
 * Usage:
 *   <AppShell><MyPage /></AppShell>
 *   <AppShell hideBottomBar><Reader /></AppShell>   // immersive pages
 *
 * If `user` isn't provided, AppShell resolves it via checkAuth() and
 * redirects to /login on failure.
 */
const AppShell = ({
  children,
  user: userProp,
  role: roleProp,
  hideBottomBar = false,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(userProp || null);
  const [realRole, setRealRole] = useState(roleProp || 'user');
  const [space, setSpace] = useState(getActiveSpace());
  const [adminViewing, setAdminViewing] = useState(getAdminViewingAs());
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [checking, setChecking] = useState(!userProp);

  // Listen for space flips so the shell re-renders tabs/badges immediately.
  useEffect(() => {
    const unsub = onSpaceChange((next, axis) => {
      if (axis === 'admin') setAdminViewing(getAdminViewingAs());
      else setSpace(next);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (userProp) {
      setUser(userProp);
      setRealRole(roleProp || userProp.role || 'user');
      setChecking(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await checkAuth();
        if (cancelled) return;
        if (!data) {
          navigate('/login');
          return;
        }
        setUser(data);
        setRealRole((roleProp || data.role || 'user').toLowerCase());
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userProp, roleProp, navigate]);

  // Writers can opt into reader space — compute the effective role used for
  // tab/action rendering. Admins and readers are unaffected.
  const role = effectiveRoleFor(realRole, space);

  // Daily streak heartbeat — fires POST /api/streak/touch once per local
  // day so streaks advance just by opening the app (not only by reading
  // a specific book).
  useStreakHeartbeat(user);

  const handleLogout = async () => {
    if (onLogout) {
      await onLogout();
      return;
    }
    await authLogout();
    navigate('/login');
  };

  // Bump bottom padding when a global audio track is loaded so the mini-player
  // (~80px tall, sitting 64px above the bottom) never overlaps page content.
  // MUST be called before any early-return to satisfy rules-of-hooks.
  const { track: globalTrack } = useAudioPlayer();

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0b14] via-[#151229] to-[#0a0b14] flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  const hasMiniPlayer = !!globalTrack;
  const padCls = hideBottomBar
    ? hasMiniPlayer
      ? 'pb-32'
      : 'pb-0'
    : hasMiniPlayer
      ? 'pb-44'
      : 'pb-24';

  // Bold visual band when admin is viewing as a non-admin role. Sits
  // directly under the TopBar so it's the first thing the eye lands on.
  // We also dim/tint with a 1px ring on the body for unmissable presence.
  const showAdminBanner = realRole === 'admin' && adminViewing && adminViewing !== 'admin';
  const bannerCfg = adminViewing === 'reader'
    ? { from: 'from-indigo-700', to: 'to-indigo-500', label: 'Reader', icon: '📖', ring: 'ring-indigo-500/40' }
    : { from: 'from-amber-600', to: 'to-amber-400', label: 'Scribe', icon: '✍️', ring: 'ring-amber-500/40' };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#0a0b14] via-[#151229] to-[#0a0b14] flex flex-col ${showAdminBanner ? `ring-2 ${bannerCfg.ring} ring-inset` : ''}`}>
      <OnboardingTour user={user} />
      <TopBar
        role={role}
        realRole={realRole}
        user={user}
        onLogout={handleLogout}
        onOpenGrantDialog={() => setGrantDialogOpen(true)}
      />
      {/* Admin "viewing as X" color band — purple/indigo/amber */}
      {showAdminBanner && (
        <div
          className={`bg-gradient-to-r ${bannerCfg.from} ${bannerCfg.to} text-white text-[11px] sm:text-xs font-bold tracking-wider uppercase shadow-lg`}
          data-testid={`admin-viewing-band-${adminViewing}`}
        >
          <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <span className="text-base">{bannerCfg.icon}</span>
              Viewing as <span className="font-black">{bannerCfg.label}</span>
              <span className="hidden sm:inline opacity-75 font-medium normal-case">— admin powers still active</span>
            </span>
            <button
              type="button"
              onClick={() => {
                // Switching back to admin via the band — read-only handler, the
                // toggle in TopBar is the canonical control.
                import('../utils/spaceContext').then(({ setAdminViewingAs }) => {
                  setAdminViewingAs('admin');
                  navigate('/admin-dashboard');
                });
              }}
              className="hidden sm:inline-flex items-center gap-1 px-3 py-0.5 rounded-full bg-black/30 hover:bg-black/50 text-white text-[10px] font-bold transition-colors"
              data-testid="admin-viewing-band-exit"
            >
              ⚡ Back to Admin
            </button>
          </div>
        </div>
      )}
      {PAYMENTS_DISABLED && (
        <div
          className="border-y border-amber-300/35 bg-amber-400/10 px-4 py-2 text-center text-xs font-bold text-amber-100"
          role="status"
          data-testid="payments-disabled-banner"
        >
          Staging/free-launch preview: payment checkouts are unavailable. No real-money transaction can be started here.
        </div>
      )}
      <GrantCoinsDialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)} />
      <div
        className={`flex-1 ${padCls} ${hideBottomBar ? 'flex flex-col min-h-0' : ''}`}
        data-testid="app-shell-content"
      >
        {/* Global admin announcement banner — floats above page content
            on every authed surface (hidden inside immersive reader/player
            surfaces via hideBottomBar). Self-dismisses if no active
            announcement is found. */}
        {user && !hideBottomBar && (
          <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-3">
            <AnnouncementBanner />
          </div>
        )}
        {children}
        {/* Tiny self-heal action — visible at the bottom of every page so
            users can wipe stale caches without needing dev tools. Hidden on
            the immersive reader/player surfaces because they manage their
            own viewport and an extra row pushes content off-screen. */}
        {user && !hideBottomBar && (
          <div className="max-w-7xl mx-auto px-4 pt-6 pb-4 flex justify-end">
            <ForceRefreshButton />
          </div>
        )}
        {/* Voice navigation — floating mic, browser-native Web Speech API.
            Mounted globally inside AppShell so every authed page can use it.
            Hides itself on unsupported browsers. Feb 26 2026. */}
        {user && <VoiceNavButton />}
        {user && hideBottomBar && (
          /* Reader/Player still get the self-heal, but inline at the very
              bottom inside their own scroll region — see ForceRefreshButton
              consumers. We omit the global row so the page never overflows. */
          null
        )}
      </div>
      {!hideBottomBar && <BottomBar role={role} realRole={realRole} adminViewing={adminViewing} />}
      <WhatsNewModal user={user} />
      <WriterContractSignModal user={user} />
      <WriterContractPopup user={user} />
      {role === 'admin' && <AdminVaultNotifier />}
      {/* InstallPromptBanner removed — consolidated into the global
          <InstallPWA /> mounted in App.js so there's a single, smarter
          source of truth for home-screen install nudges. */}
    </div>
  );
};

export default AppShell;
